import { useContext, useEffect, useRef, useState } from "react";
import HttpStatus from "@cayo/shared/api/types/HttpStatus";
import { AppContext } from "@cayo/shared/components/App/appContext";
import logger from "@cayo/shared/libs/logger";
import { getError } from "@cayo/shared/libs/oDataClient";
import { endpoints } from "@cayo/shared/services/endpoints.service";
import { ITokenStorage } from "@cayo/shared/settings/tokenStorage";
import { OAuthClient } from "@cayo/shared/types/manifest";
import { getRequestUrl } from "./get-request-url";
import { getStubResponse } from "./stub-response";
import authRecoveryService from "../../services/auth-recovery.service";

const log = logger.getLogger("useAuth");

interface UseAuthParams {
  tokenEndpoint: string;
  tokenStorage: ITokenStorage;
  oauthClient?: OAuthClient;
  authorityUri?: string;
}

const useAuth = ({ tokenEndpoint, tokenStorage, oauthClient, authorityUri }: UseAuthParams) => {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const isRedirected = useRef(false);
  const { showServerUnavailable } = useContext(AppContext);

  useEffect(() => {
    const addBearer = (token: string, init: RequestInit | undefined) => {
      const newInit: RequestInit = {
        ...(init || {}),
        credentials: "same-origin",
        headers: {
          ...(init?.headers || {}),
          Authorization: `Bearer ${token}`,
          Accept: "application/json",
          "Content-Type": "application/json",
        },
        mode: "cors",
      };

      if (endpoints.isAdminMode) {
        (newInit!.headers! as any).csmclient = "admin";
      }

      return newInit;
    };

    (function () {
      log.debug("fetch monkey patching");
      const originalFetch = fetch;

      window.fetch = async function (input: RequestInfo | URL, init?: RequestInit) {
        const _this = this;

        const stubResponse = getStubResponse(input, oauthClient?.clientId);
        if (stubResponse) {
          return stubResponse;
        }

        const isAuthUrl = getRequestUrl(input).includes("/auth/");
        if (isAuthUrl) {
          return originalFetch.apply(this, [input as any, init]);
        }

        const token = tokenStorage.getToken();
        const newInit = token?.access_token ? addBearer(token.access_token, init) : init;

        try {
          const data = await originalFetch.apply(this, [input as any, newInit]);
          if (data.status === HttpStatus.UNAUTHORIZED || data.status === HttpStatus.FORBIDDEN) {
            const responseClone = data.clone();
            let response: any = undefined;
            try {
              response = await data.json();
            } catch {
              // skip errors
            }

            if (response?.source || data.status === HttpStatus.FORBIDDEN) {
              return Promise.resolve(responseClone);
            }

            const emptyResponse: Response = {
              status: 200,
              json: async () => null,
              statusText: "",
              text: async () => "",
              ok: true,
            } as Response;

            const recoveryResult = await authRecoveryService.recoverAuthSession({
              response: data,
              tokenEndpoint,
              originalFetch,
              oauthClient,
              authorityUri,
            });

            if (recoveryResult.action === "retry-request") {
              const newToken = tokenStorage.getToken()?.access_token;
              if (!newToken) {
                return Promise.resolve(emptyResponse);
              }

              const newInit = addBearer(newToken, init);
              const res = await originalFetch.apply(_this, [input as any, newInit]);
              if (res.status === HttpStatus.UNAUTHORIZED) {
                return Promise.reject(res);
              }

              return res;
            }

            if (!isRedirected.current) {
              isRedirected.current = true;
              return Promise.reject(responseClone);
            }

            log.debug("token has expired. skip redirecting");
            return Promise.resolve(emptyResponse);
          } else {
            return data;
          }
        } catch (e) {
          log.debug(e);
          const error = await getError(e);
          showServerUnavailable({ message: error, state: "failed" });

          throw e;
        }
      };
    })();

    setIsLoggedIn(!!tokenStorage.getToken()?.access_token);

    return tokenStorage.SaveEvent.on((token) => {
      setIsLoggedIn(!!token?.access_token);
    });
  }, []);

  useEffect(() => {
    if (isLoggedIn) {
      log.debug("set isRedirected to false");
      isRedirected.current = false;
    }
  }, [isLoggedIn]);

  return { isLoggedIn };
};

export default useAuth;
